// Raycast-based "look at a thing and press E" interaction system.
//
// Every interactable registers a mesh (or group) to raycast against, a dynamic
// label, an action, and an optional enabled() gate. Each frame the system
// raycasts from screen-centre and reports the nearest in-range hit so the HUD
// can prompt. main.ts owns the key binding and calls use().

import THREE from "./three.js";
import type { Object3D, Camera } from "three";

export interface Interactable {
  /** Mesh/group raycast against. Children are included. */
  object: Object3D;
  /** Prompt text, evaluated each frame (e.g. "Open door" vs "Close door"). */
  label: () => string;
  /** Called when the player presses E while looking at this. */
  onUse: () => void;
  /** If present and returns false, the target is skipped entirely. */
  enabled?: () => boolean;
  /** Max distance in metres (default 2.2). */
  range?: number;
}

export class InteractSystem {
  private items: Interactable[] = [];
  private ray = new THREE.Raycaster();
  private centre = new THREE.Vector2(0, 0);
  current: Interactable | null = null;

  add(it: Interactable): Interactable {
    this.items.push(it);
    return it;
  }
  remove(it: Interactable) {
    const i = this.items.indexOf(it);
    if (i >= 0) this.items.splice(i, 1);
  }

  /** Recompute the focused interactable. Call once per frame. */
  update(camera: Camera) {
    this.ray.setFromCamera(this.centre, camera);
    let best: Interactable | null = null;
    let bestDist = Infinity;
    for (const it of this.items) {
      if (it.enabled && !it.enabled()) continue;
      const hits = this.ray.intersectObject(it.object, true);
      if (!hits.length) continue;
      const d = hits[0].distance;
      const range = it.range ?? 2.2;
      if (d <= range && d < bestDist) {
        bestDist = d;
        best = it;
      }
    }
    this.current = best;
  }

  use() {
    if (this.current && (!this.current.enabled || this.current.enabled())) {
      this.current.onUse();
    }
  }
}
